export type TrustedCaregiverOnboardingStatus = 'INVITED' | 'IN_PROGRESS' | 'ELIGIBILITY_REVIEW' | 'UNDER_REVIEW' | 'COMPLETE';

export const TRUSTED_CAREGIVER_CHECKLIST_FLAG = 'sc-trusted-caregiver-checklist-query-enabled';

export type TrustedCaregiverChecklistItemType = 'PHOTO' | 'AVAILABILITY' | 'REVIEWS' | 'CPR_CERTIFICATE' | 'VIDEO';

export type TrustedCaregiverChecklistItem = {
  type: TrustedCaregiverChecklistItemType;
  complete: boolean;
};

export type TrustedCaregiver = {
  onboardingStatus: TrustedCaregiverOnboardingStatus;
  checklist: TrustedCaregiverChecklistItem[];
};

export const CHECKLIST_ITEM_ORDER: TrustedCaregiverChecklistItemType[] = [
  'PHOTO',
  'AVAILABILITY',
  'REVIEWS',
  'VIDEO',
  'CPR_CERTIFICATE',
];

export const CHECKLIST_ITEM_COPY: Record<TrustedCaregiverChecklistItemType, { title: string; content: string }> = {
  PHOTO: {
    title: 'Add a profile photo',
    content: 'Add a clear, friendly photo to help families get to know you.',
  },
  AVAILABILITY: {
    title: 'Update your availability',
    content: 'Let families know when you’re free to work.',
  },
  REVIEWS: {
    title: 'Meet the ratings and reviews requirement',
    content: 'Have at least 3 reviews at 4 stars or higher.',
  },
  VIDEO: {
    title: 'Learn about Sittercity’s best practices',
    content: 'Watch the video',
  },
  CPR_CERTIFICATE: {
    title: 'Add your CPR/First Aid certification',
    content: 'Include your training in your bio.',
  },
};

export const COPY = {
  inProgressHeader: 'Your Trusted Caregiver Checklist',
  underReviewHeader: 'Your Trusted Caregiver Status',
  completeHeader: 'You’re a Trusted Caregiver',
  tipsHeader: 'Maintain your Trusted Caregiver Program status',
  inProgressSubtext: 'Complete these steps to become a Trusted Sitter.',
  underReviewSubtext:
    'Our team is reviewing your profile to ensure you’ve completed all the required steps. Keep an eye on your inbox for next steps.',
  completeSubtext:
    'You’re part of the Trusted Caregiver Program. Families will see your Trusted Sitter badge on your profile as they look for care.',
  learnMoreLabel: 'Learn more about the Trusted Caregiver Program.',
} as const;

/** Maintenance tips shown when onboardingStatus is COMPLETE */
export type CompleteTipAction = 'photo' | 'review' | 'availability' | 'cpr';

export type CompleteTip = {
  id: string;
  label: string;
  action?: CompleteTipAction;
};

export const COMPLETE_TIPS: CompleteTip[] = [
  { id: 'response-time', label: 'Respond to messages from families within 24 hours' },
  { id: 'rating', label: 'Maintain an average rating of 4 stars or higher', action: 'review' },
  { id: 'cpr', label: 'Maintain a CPR/First Aid Certification', action: 'cpr' },
  {
    id: 'availability',
    label: 'Keep your availability up to date for at least the next 90 days.',
    action: 'availability',
  },
  { id: 'cancellation', label: 'Follow the Cancellation and No-Show Policy' },
  { id: 'monitoring', label: 'Pass continuous criminal monitoring' },
  { id: 'photo', label: 'Keep your profile photo up to date', action: 'photo' },
];

export const CONFETTI_SESSION_KEY = 'trusted_caregiver_confetti_shown';

export const IOS_APP_URL = 'https://apps.apple.com/us/app/find-child-care-sittercity/id900660780';

export function computeCompletenessScore(checklist: TrustedCaregiverChecklistItem[]): number {
  if (checklist.length === 0) return 0;
  const completedCount = checklist.filter((item) => item.complete).length;
  return Math.round((completedCount / checklist.length) * 100);
}

type TrustedCaregiverApiPayload = {
  onboardingStatus?: TrustedCaregiverOnboardingStatus;
  checklist: TrustedCaregiverChecklistItem[];
};

/** Maps the trustedCaregiver GraphQL payload into presentation state. */
export function mapTrustedCaregiverFromApi(payload: TrustedCaregiverApiPayload | null | undefined): TrustedCaregiver | null {
  if (!payload?.checklist) return null;

  const checklist = payload.checklist;
  const derivedStatus: TrustedCaregiverOnboardingStatus =
    checklist.length > 0 && checklist.every((item) => item.complete) ? 'UNDER_REVIEW' : 'IN_PROGRESS';

  return {
    onboardingStatus: payload.onboardingStatus ?? derivedStatus,
    checklist,
  };
}
